import dayjs, { Dayjs } from "dayjs";
import { ProjectTypes } from "../../common/constants";
import { ReportFilters } from "./datacenterApi";
import { InvoiceReportFilters } from "./InvoiceReport/InvoiceReport";
import { ItemsReportFilters } from "./ItemsReport/ItemsReport";
import { ProposalsReportFilters } from "./ProposalsReport/ProposalsReport";
import { ManHoursReportFilters } from "./ManHours/ManHoursReport";
import { EstimatesReportFilters } from "./EstimatesReport/EstimatesReport";

type Enum<T> = T[keyof T]
export const DatacenterReportTypes = {
  Proposal: 1,
  ManHours: 2,
  InvoiceVolume: 3,
  Items: 4,
  VisitNotes: 5,
  Estimates: 6
}
export type DatacenterReportType = Enum<typeof DatacenterReportTypes>

export const getDatacenterReportTypeNames = (type: DatacenterReportType): string => {
  return {
    [DatacenterReportTypes.Proposal]: 'Proposal',
    [DatacenterReportTypes.ManHours]: 'Man Hours',
    [DatacenterReportTypes.InvoiceVolume]: 'Invoice',
    [DatacenterReportTypes.Items]: 'Items',
    [DatacenterReportTypes.VisitNotes]: 'VisitNotes',
    [DatacenterReportTypes.Estimates]: 'Estimates',
  }[type]
}

const defaultStartDate = dayjs().subtract(6, 'month');

export type ReportFilterViewModel = Omit<ReportFilters, 'filters'>
  & { filters: ProposalsReportFilters | ItemsReportFilters | InvoiceReportFilters | ManHoursReportFilters | EstimatesReportFilters }

export const getFiltersFromJson = (json: string): ProposalsReportFilters | ItemsReportFilters | InvoiceReportFilters | ManHoursReportFilters | EstimatesReportFilters => {
  try {
    const filter = JSON.parse(json)
    return {
      ...filter,
      startDate: dayjs(filter.startDate),
      endDate: dayjs(filter.endDate)
    }
  } catch {
    return {
      startDate: defaultStartDate,
      endDate: dayjs(),
      salesPersonIds: [],
      projectTypeIds: [],
      divisionIds: [],
      dateRange: 'custom',
      searchTerm: '',
      archived: false
    }
  }
}

export const getProjectSlug = (id: number, _name: string): string => {
  return `${id}`.trim();
};

export const getProjectUrl = (id: number, type: number, sold: boolean, name: string): string => {
  const slug = getProjectSlug(id, name);
  switch (type) {
    case ProjectTypes.Project:
      return sold ? `projects/${slug}` : `project-estimate/${slug}`;
    case ProjectTypes.Ticket:
      return sold ? `tickets/${slug}` : `ticket-estimate/${slug}`;
    case ProjectTypes.Maintenance:
      return sold ? `maintenance/${slug}` : `maintenance-estimate/${slug}`;
    default:
      return "unknown";
  }
};

export type DateRanges = 'last24Hours' | 'last7Days' | 'lastMonth' | 'thisYear' | 'lastYear' | 'allDates' | 'custom';

export type DateRangeOption = {
  label: string;
  value: DateRanges;
  range: [Dayjs, Dayjs] | null;
}

export const dateRanges: DateRangeOption[] = [
  {
    label: 'Last 24 Hours',
    value: 'last24Hours',
    range: [dayjs().subtract(1, 'day'), dayjs()],
  },
  {
    label: 'Last 7 Days', 
    value: 'last7Days',
    range: [dayjs().subtract(7, 'days'), dayjs()],
  },
  {
    label: 'Last Month',
    value: 'lastMonth',
    range: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
  },
  {
    label: 'This Year',
    value: 'thisYear',
    range: [dayjs().startOf('year'), dayjs()],
  },
  {
    label: 'Last Year',
    value: 'lastYear',
    range: [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')],
  },
  {
    label: 'All Dates',
    value: 'allDates',
    range: [dayjs().subtract(10, 'year').startOf('year'), dayjs().add(20, 'year').endOf('year')],
  },
  {
    label: 'Custom Range',
    value: 'custom',
    range: null,
  },
];

export const getRangeName = (range: DateRanges): string => {
  const rangeNames: Record<DateRanges, string> = {
    last24Hours: 'Last 24 Hours',
    last7Days: 'Last 7 Days',
    lastMonth: 'Last Month',
    thisYear: 'This Year',
    lastYear: 'Last Year',
    allDates: 'All Dates',
    custom: 'Custom Range',
  };
  return rangeNames[range];
}

export const getDateFromDateRange = (range: DateRanges): [Dayjs, Dayjs] => {
  const matchingRange = dateRanges.find((x) => x.value === range);
  return matchingRange?.range || [defaultStartDate, dayjs()];
}

export const formatDateRange = (startDate: Dayjs, endDate: Dayjs): string => {
  const startYear = startDate.year();
  const endYear = endDate.year();
  const currentYear = dayjs().year();

  if(startYear === currentYear && endYear === currentYear) return `${startDate.format('MMM D')} - ${endDate.format('MMM D')}`
  if(startYear < currentYear && endYear === currentYear) return `${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D')}`

  return `${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`;
};

export const updateFilterDates = (filters: InvoiceReportFilters | ItemsReportFilters | ProposalsReportFilters): string => {
  const condition = filters.dateRange !== 'custom'
  const updatedStartDate = condition
    ? ''
    : filters.startDate;
  const updatedEndDate = condition
    ? ''
    : filters.endDate;

  return JSON.stringify({
    ...filters,
    startDate: updatedStartDate,
    endDate: updatedEndDate,
  });
}
